import PropTypes from 'prop-types';
import {observer, PropTypes as MobXPropTypes} from 'mobx-react';
import React from 'react';

import autobind from 'common/decorators/autobind.js';

import {
    DIRECTORY_SEARCH_RESULT_CARD_LINK,
    PROFILE_CARD,
    CALL_NOW_BUTTON_BANNER_ADS,
} from 'core/constants.js';

import {DirectoryStoreContext} from 'core/stores/RootStore.js';
import {getBookingAvailability} from 'core/services/Mh1Service';

import CallNowBtn from 'core/components/v2/CallNowBtn.js';
import CustomBookingBtn from 'core/components/v2/CustomBookingBtn.js';
import MH1BookNowBtn from 'core/components/v2/MH1BookNowBtn.js';
import {CardV2} from '@HealthShareAU/hs-component-library';

export default
@observer
class ProfileCard extends React.Component {
    static contextType = DirectoryStoreContext;
    static propTypes = {
        avatar: PropTypes.string,
        customBookingDataUrl: PropTypes.string,
        displayName: PropTypes.string.isRequired,
        hasIntegratedBookings: PropTypes.bool,
        hasFees: PropTypes.bool,
        healthFund: PropTypes.object,
        id: PropTypes.number.isRequired,
        initials: PropTypes.string.isRequired,
        interest: PropTypes.shape({
            id: PropTypes.number,
        }),
        interestsDescription: PropTypes.string,
        isEnhanced: PropTypes.bool.isRequired,
        isReferrals: PropTypes.bool.isRequired,
        offersHicaps: PropTypes.bool.isRequired,
        offersTelehealth: PropTypes.bool.isRequired,
        phone: PropTypes.string,
        practice: PropTypes.shape({
            address: PropTypes.string.isRequired,
            id: PropTypes.number.isRequired,
            localityName: PropTypes.string,
            localityState: PropTypes.string,
            name: PropTypes.string.isRequired,
            phones: MobXPropTypes.arrayOrObservableArrayOf(
                PropTypes.shape({
                    number: PropTypes.string.isRequired,
                }),
            ),
            practiceUrl: PropTypes.string,
        }).isRequired,
        profileUrl: PropTypes.string.isRequired,
        specialties: PropTypes.oneOfType([
            PropTypes.arrayOf(
                PropTypes.shape({
                    name: PropTypes.string.isRequired,
                }),
            ),
            PropTypes.object, // ObservableArray
        ]).isRequired,
        specialty: PropTypes.shape({
            isMedical: PropTypes.bool.isRequired,
        }).isRequired,
        ratings: PropTypes.shape({
            average: PropTypes.number.isRequired,
            count: PropTypes.number.isRequired,
        }),
    };

    constructor(props) {
        super(props);

        this.state = {
            modalDisplayed: false,
        };

        this.modalProps = {
            closeModal: this.closeModal,
            colorTheme: null,
            contactId: props.id,
            contactName: props.displayName,
            isEnhanced: props.isEnhanced,
            items: [props.practice].map((item) => {
                item.type = 'profile';
                return item;
            }),
            phoneNumber: props.phone,
            type: 'profile',
        };
    }

    @autobind
    closeModal() {
        this.setState({modalDisplayed: false});
    }

    @autobind
    handlePhoneClick() {
        this.setState({modalDisplayed: true});
    }

    get profileUrl() {
        const {healthFund, interest, profileUrl} = this.props;
        let url = profileUrl;

        const urlParams = {};
        if (healthFund?.id > 0) {
            urlParams['health_fund'] = healthFund.id;
        }
        if (interest?.id) {
            urlParams['special_interest'] = interest.id;
        }

        const queryParams = Object.entries(urlParams)
            .map(([k, v]) => `${k}=${v}`)
            .join('&');

        return `${url}${queryParams ? `?${queryParams}` : ''}`;
    }

    @autobind
    getSpecialtyName(specialty) {
        const {
            rootStore: {
                paramStore: {isProUser, isReferrals},
            },
        } = this.context;
        if (isProUser || isReferrals) {
            return specialty.professionalName;
        } else {
            return specialty.name;
        }
    }

    renderSpecialties() {
        const {specialties} = this.props;
        if (!specialties.length) {
            return null;
        }
        return (
            <>
                {specialties
                    .map((specialty) => {
                        return this.getSpecialtyName(specialty);
                    })
                    .join(', ')}
            </>
        );
    }

    getPhoneNumber() {
        const {phone} = this.props;
        if (!phone) {
            return null;
        }
        return {
            phone: phone,
            gaSelector: 'call-now-button-secondary',
            gaObjectType: PROFILE_CARD,
        };
    }

    getAddress() {
        if (this.props.practice.address) {
            const url = this.props.profileUrl;
            return {
                addressText: this.props.practice.address,
                gaObjectType: PROFILE_CARD,
                gaSelector: 'address-link',
                url: url && `${url}#location`,
            };
        }
    }

    getFees() {
        const {hasFees, healthFund, id, specialty} = this.props;

        const iconColor = healthFund?.colorBrand
            ? {
                  color: `#${healthFund.colorBrand}`,
              }
            : null;

        if (hasFees && healthFund?.id > 0 && specialty.isMedical) {
            return {
                feesUrl: `${this.profileUrl}#fees`,
                gaObjectType: PROFILE_CARD,
                gaSelector: 'see-out-of-pocket-costs',
            };
        }
        return null;
    }

    getRatingAverage() {
        const {healthFund, ratings} = this.props;
        const {
            rootStore: {
                paramStore: {isReferrals},
            },
        } = this.context;
        if (
            isReferrals ||
            !ratings ||
            (healthFund?.id && !healthFund?.showRatings)
        ) {
            return;
        }

        const {average} = ratings;
        return average;
    }

    getRatingCount() {
        const {healthFund, ratings} = this.props;
        const {
            rootStore: {
                paramStore: {isReferrals},
            },
        } = this.context;
        if (
            isReferrals ||
            !ratings ||
            (healthFund?.id && !healthFund?.showRatings)
        ) {
            return;
        }

        const {count} = ratings;
        return count;
    }

    renderCTA() {
        const {customBookingDataUrl} = this.props;

        const isPPHasPhone = this.props.phone;

        const {
            availabilitySummaries,
            isMh1Running,
            rootStore: {
                paramStore: {hasSkin, dataSource},
                healthFundStore: {
                    isHealthFundBookingsEnabled,
                    enabledMH1BookingIntegration,
                    healthFundThemeColor,
                },
            },
        } = this.context;

        const colorTheme =
            hasSkin && healthFundThemeColor ? healthFundThemeColor : null;

        const bookingIntegration = enabledMH1BookingIntegration(this.props);

        // MH1 has the highest priority
        if (bookingIntegration) {
            const availability = getBookingAvailability(
                availabilitySummaries,
                bookingIntegration,
            );

            return (
                <MH1BookNowBtn
                    dataSource={dataSource}
                    bookingIntegration={bookingIntegration}
                    availability={availability}
                    isMh1Running={isMh1Running}
                    colorTheme={colorTheme}
                    gaObjectType={PROFILE_CARD}
                />
            );
        }
        if (isHealthFundBookingsEnabled && !hasSkin && customBookingDataUrl) {
            return (
                <CustomBookingBtn
                    colorTheme={colorTheme}
                    gaObjectType={'directory result'}
                    customBookingDataUrl={customBookingDataUrl}
                />
            );
        }
        if (isPPHasPhone) {
            return (
                <CallNowBtn
                    colorTheme={colorTheme}
                    gaObjectType={PROFILE_CARD}
                    modalContent={{...this.modalProps, colorTheme}}
                />
            );
        }
        return null;
    }

    render() {
        const {
            avatar,
            displayName,
            isEnhanced,
            offersHicaps,
            offersTelehealth,
            interestsDescription,
        } = this.props;

        const {
            rootStore: {
                paramStore: {isStaff, isClient, hasSkin},
                healthFundStore: {healthFundThemeColor},
            },
            telehealthDirectory,
        } = this.context;

        const {modalDisplayed} = this.state;

        const directoryCardProps = {
            alt: displayName,
            avatar: avatar ? {src: avatar} : {},
            button: this.renderCTA(),
            feesChip: this.getFees(),
            dataTestId: 'profileCard',
            addressChip: this.getAddress(),
            feesColor: healthFundThemeColor,
            header: displayName,
            specialInterests: interestsDescription ? interestsDescription : '',
            enhanced: isStaff && isEnhanced,
            offersHicaps: offersHicaps,
            offersTelehealth: telehealthDirectory || offersTelehealth,
            href: this.props.profileUrl,
            phoneChip: this.getPhoneNumber(),
            rating: this.getRatingAverage(),
            numRatings: this.getRatingCount(),
            subheader: this.renderSpecialties(),
            gaObjectType: PROFILE_CARD,
            gaSelector: DIRECTORY_SEARCH_RESULT_CARD_LINK,
            colorTheme:
                hasSkin && healthFundThemeColor ? healthFundThemeColor : null,
            onPhoneClick: () => this.handlePhoneClick(),
        };

        this.modalProps['colorTheme'] =
            hasSkin && healthFundThemeColor ? healthFundThemeColor : null;
        return (
            <>
                {modalDisplayed && this.context.renderModal(this.modalProps)}
                <CardV2 {...directoryCardProps} />
            </>
        );
    }
}
